import { createContext, useCallback, useContext, useEffect, useReducer, useState } from 'react'
import type { Dispatch, ReactNode, SetStateAction } from 'react'

import type {
	SurveyParams,
	SurveyQuestion,
	SurveySubmissionsState
} from '@lib/types/subscription-box'
import { clearSurveySession, readSurveySession, writeSurveySession } from '@lib/hooks/useSurveySession'

// Typeof return useNewSurveyStateManager
type UseManagerResult = ReturnType<typeof useNewSurveyStateManager>

// New survey context with initial values
const NewSurveyContext = createContext<UseManagerResult>({
	survey: {
		surveyId: 0,
		surveyQuestions: []
	},
	surveyState: {
		currentIndex: 0,
		surveyProgress: 0
	},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	nextQuestion: () => {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	previousQuestion: () => {},
	surveySubmissions: [],
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	handleSurveySubmission: () => {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	handleSurveySkipSubmission: () => {},
	nextButtonDisabled: false,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setNextButtonDisabled: () => {}
})

export { NewSurveyContext }

// Reducer action type
type ActionType = { type: 'NEXT' | 'PREVIOUS' }

type SurveyStateParams = {
	currentIndex: number
	surveyProgress: number
}

// Manages new survey content state — decoupled from cart and session
function useNewSurveyStateManager(survey: SurveyParams): {
	survey: SurveyParams
	surveyState: SurveyStateParams
	nextQuestion: () => void
	previousQuestion: () => void
	surveySubmissions: SurveySubmissionsState[]
	handleSurveySubmission: (
		surveyQuestion: SurveyQuestion,
		newVal:
			| {
					optionValue: string
					id?: number
			  }
			| any[]
	) => void
	handleSurveySkipSubmission: (surveyQuestionId: string) => void
	nextButtonDisabled: boolean
	setNextButtonDisabled: Dispatch<SetStateAction<boolean>>
} {
	const { surveyQuestions } = survey || {}

	// Survey reducer
	const [surveyState, dispatch] = useReducer(
		(state: SurveyStateParams, action: ActionType): SurveyStateParams => {
			const { currentIndex } = state

			switch (action.type) {
				case 'NEXT': {
					if (
						surveyQuestions?.[currentIndex]?.id ===
						surveyQuestions?.[surveyQuestions.length - 1]?.id
					) {
						return state
					}

					const newSurveyProgress = (currentIndex + 2) / ((surveyQuestions?.length || 0) + 1)
					return {
						currentIndex: currentIndex + 1,
						surveyProgress: newSurveyProgress
					}
				}
				case 'PREVIOUS': {
					if (surveyQuestions?.[currentIndex]?.id === surveyQuestions?.[0]?.id) {
						return state
					}

					const newSurveyProgress = currentIndex / ((surveyQuestions?.length || 0) + 1)
					return {
						currentIndex: currentIndex - 1,
						surveyProgress: newSurveyProgress
					}
				}
				default:
					return state
			}
		},
		null,
		() => {
			const saved = readSurveySession(survey?.surveyId)
			const idx = saved?.currentIndex ?? 0
			return {
				currentIndex: idx,
				surveyProgress: (idx + 1) / ((surveyQuestions?.length || 0) + 1)
			}
		}
	)

	const nextQuestion = useCallback(() => {
		dispatch({ type: 'NEXT' })
	}, [])

	const previousQuestion = useCallback(() => {
		dispatch({ type: 'PREVIOUS' })
	}, [])

	// User survey submissions — seeded from sessionStorage when available
	const [surveySubmissions, setSurveySubmissions] = useState<SurveySubmissionsState[]>(() => {
		const base = surveyQuestions?.map(({ id, body }) => ({
			question: body,
			questionId: id,
			response: [] as string[],
			optionId: [] as string[],
			additionalData: ''
		})) ?? []
		const saved = readSurveySession(survey?.surveyId)
		if (!saved) return base
		// Merge saved answers by questionId — handles question reordering gracefully
		return base.map(item => {
			const savedItem = saved.submissions.find(s => s.questionId === item.questionId)
			return savedItem ? { ...item, response: savedItem.response, optionId: savedItem.optionId ?? [] } : item
		})
	})

	const handleSurveySubmission = useCallback(
		(surveyQuestion: any, newVal: any) => {
			setSurveySubmissions((prev: SurveySubmissionsState[]) => {
				// For multiselect
				if ((surveyQuestion?.type === 'multiselect' || surveyQuestion?.type === 'inline_multiselect') && Array.isArray(newVal)) {
					const idArray = newVal
						?.filter(({ active }: { active: boolean }) => active)
						?.map(({ id }: { id: number }) => id)

					const valueArray = newVal
						?.filter(({ active }: { active: boolean }) => active)
						?.map(({ optionValue }: { optionValue: string }) => optionValue)

					return prev.map(
						({ question, questionId, response, optionId, additionalData }) => ({
							question,
							questionId,
							response: surveyQuestion?.id === questionId ? valueArray : response,
							optionId: surveyQuestion?.id === questionId ? idArray : optionId,
							additionalData
						})
					)
				}
				// For RangeSlider and RadioSelect
				return prev.map(
					({ question, questionId, response, optionId, additionalData }) => ({
						question,
						questionId,
						response: surveyQuestion?.id === questionId ? [newVal?.optionValue] : response,
						optionId: surveyQuestion?.id === questionId ? [newVal?.id] : optionId,
						additionalData
					})
				)
			})
		},
		[setSurveySubmissions]
	)

	// Resets current question value when skipped
	const handleSurveySkipSubmission = useCallback(
		(surveyQuestionId: any) => {
			setSurveySubmissions((prev: SurveySubmissionsState[]) =>
				prev.map(({ question, questionId, response, additionalData, optionId }) => ({
					question,
					questionId,
					response: surveyQuestionId === questionId ? [] : response,
					optionId,
					additionalData
				}))
			)
		},
		[setSurveySubmissions]
	)

	// Next button disabled status
	const [nextButtonDisabled, setNextButtonDisabled] = useState(false)

	// Persist index + answers to sessionStorage on every navigation or answer change
	useEffect(() => {
		writeSurveySession({
			surveyId: survey?.surveyId ?? 0,
			currentIndex: surveyState.currentIndex,
			submissions: surveySubmissions.map(({ questionId, response, optionId }) => ({
				questionId,
				response,
				optionId
			}))
		})
	}, [surveyState.currentIndex, surveySubmissions, survey?.surveyId])

	return {
		survey,
		surveyState,
		nextQuestion,
		previousQuestion,
		surveySubmissions,
		handleSurveySubmission,
		handleSurveySkipSubmission,
		nextButtonDisabled,
		setNextButtonDisabled
	}
}

// Simplified provider — accepts surveyData directly, no session/customer dependency
export function NewSurveyProvider({
	children,
	surveyData
}: {
	children: ReactNode
	surveyData: SurveyParams
}) {
	return (
		<NewSurveyContext.Provider value={useNewSurveyStateManager(surveyData)}>
			{children}
		</NewSurveyContext.Provider>
	)
}

// Hook exports
export const useNewSurvey = (): UseManagerResult['survey'] => {
	const { survey } = useContext(NewSurveyContext)
	return survey
}

export const useNewSurveyState = (): UseManagerResult['surveyState'] => {
	const { surveyState } = useContext(NewSurveyContext)
	return surveyState
}

export const useNewNextQuestion = (): UseManagerResult['nextQuestion'] => {
	const { nextQuestion } = useContext(NewSurveyContext)
	return nextQuestion
}

export const useNewPreviousQuestion = (): UseManagerResult['previousQuestion'] => {
	const { previousQuestion } = useContext(NewSurveyContext)
	return previousQuestion
}

export const useNewSurveySubmissions = (): UseManagerResult['surveySubmissions'] => {
	const { surveySubmissions } = useContext(NewSurveyContext)
	return surveySubmissions
}

export const useHandleNewSurveySubmissions = (): UseManagerResult['handleSurveySubmission'] => {
	const { handleSurveySubmission } = useContext(NewSurveyContext)
	return handleSurveySubmission
}

export const useHandleNewSurveySkipSubmission = (): UseManagerResult['handleSurveySkipSubmission'] => {
	const { handleSurveySkipSubmission } = useContext(NewSurveyContext)
	return handleSurveySkipSubmission
}

export const useNewNextButtonDisabled = (): UseManagerResult['nextButtonDisabled'] => {
	const { nextButtonDisabled } = useContext(NewSurveyContext)
	return nextButtonDisabled
}

export const useNewSetNextButtonDisabled = (): UseManagerResult['setNextButtonDisabled'] => {
	const { setNextButtonDisabled } = useContext(NewSurveyContext)
	return setNextButtonDisabled
}
